/**
 * INSOLECH brand color tokens — full semantic scale.
 *
 * Source palette (locked):
 *   - Primary:   #26657A (deep teal — confident, calm, professional)
 *   - Secondary: #FAF8F7 (warm off-white — premium, lifestyle)
 *
 * The Designer extends these into a complete semantic system:
 *   - Brand teal scale 50→950 for surfaces, accents, and overlays
 *   - Sand neutral ramp built around the warm off-white (NOT cool gray)
 *     so backgrounds feel inviting on the marketing site and warm-but-clean
 *     in the data-dense app
 *   - Performance accent: a warm clay/terracotta that pairs with the teal
 *     for the "lifestyle/movement" surfaces (Running, Padel, Everyday)
 *     without cluttering the app palette
 *   - Feedback ramps (success / warning / error / info) tuned to AA contrast
 *     against both surfaces.base (#FAF8F7) and surfaces.elevated (#FFFFFF)
 *
 * RTL: colors are direction-agnostic — no reds/greens that imply directional
 * meaning. State-machine status colors are duplicated by icon + label for
 * accessibility (no color-only signal).
 *
 * Usage convention:
 *   - Always reach for semantic aliases (text.primary, surface.elevated,
 *     feedback.success, status.in_production) in components
 *   - Raw scale tokens (brand.500, sand.200) are used inside the design
 *     system to compose new aliases — never sprayed across product code
 */

const teal = {
  50: '#EEF7FA',
  100: '#D6EBF1',
  200: '#A9D2DE',
  300: '#7CB6C8',
  400: '#4F95B0',
  500: '#26657A', // primary brand
  600: '#205768',
  700: '#194553',
  800: '#13343F',
  900: '#0C232A',
  950: '#061319',
} as const;

// Warm sand neutral — anchored to #FAF8F7 (the locked secondary).
// Built to pair with #26657A; tested for AA contrast on every text token.
const sand = {
  50: '#FAF8F7', // surface.base
  100: '#F2EFEC',
  200: '#E1DCD6', // border.default
  300: '#C7BFB6', // border.strong
  400: '#A99F94',
  500: '#8B8175',
  600: '#6E6459',
  700: '#544B42',
  800: '#3B342D',
  900: '#221E1A',
  950: '#13110F',
} as const;

// Performance accent — a warm clay/terracotta. Used sparingly on the
// marketing site for athletic/lifestyle surfaces. Pairs with teal at AA.
const clay = {
  50: '#FBF1EA',
  100: '#F5DDCB',
  200: '#EBBC9C',
  300: '#DF976A',
  400: '#D17742',
  500: '#B85F2A', // accent
  600: '#9A4F23',
  700: '#7B3F1C',
  800: '#5C2F15',
  900: '#3E200F',
} as const;

const success = {
  50: '#E8F5EF',
  100: '#C5E5D3',
  200: '#8FCCAC',
  300: '#5EB387',
  400: '#2F9B68',
  500: '#0F8A5F', // primary success
  600: '#0C7350',
  700: '#0A5C40',
  800: '#074530',
  900: '#042E20',
} as const;

const warning = {
  50: '#FBF2DF',
  100: '#F6E0AC',
  200: '#EFC571',
  300: '#E5A436',
  400: '#D88B14',
  500: '#C77700', // primary warning
  600: '#A66400',
  700: '#855000',
  800: '#643D00',
  900: '#432900',
} as const;

const error = {
  50: '#FBEAE9',
  100: '#F4C5C2',
  200: '#E59590',
  300: '#D6655F',
  400: '#CC433D',
  500: '#C9302C', // primary error
  600: '#A82724',
  700: '#871F1D',
  800: '#651716',
  900: '#440F0E',
} as const;

const info = {
  50: '#EAF1FB',
  100: '#C5D8F2',
  200: '#94B5E6',
  300: '#5F8FD9',
  400: '#3F86DC',
  500: '#2E7DD7', // primary info
  600: '#2667B4',
  700: '#1F5390',
  800: '#173E6C',
  900: '#0F2949',
} as const;

/**
 * Status enum colors — line_status / order_status from Phase 1 §07.
 * Used by StatusBadge component. Each status has:
 *   - bg:   tint background for the chip
 *   - fg:   text color (AA against bg)
 *   - dot:  optional indicator dot (saturated for kanban swimlanes)
 */
const status = {
  draft: { bg: sand[100], fg: sand[700], dot: sand[500] },
  submitted: { bg: info[50], fg: info[700], dot: info[500] },
  in_review: { bg: warning[50], fg: warning[700], dot: warning[500] },
  assigned_to_provider: { bg: teal[50], fg: teal[700], dot: teal[500] },
  in_production: { bg: teal[100], fg: teal[800], dot: teal[600] },
  quality_check: { bg: clay[50], fg: clay[700], dot: clay[500] },
  in_delivery: { bg: info[100], fg: info[800], dot: info[600] },
  delivered: { bg: success[50], fg: success[700], dot: success[500] },
  cancelled: { bg: error[50], fg: error[700], dot: error[500] },
  returned: { bg: sand[200], fg: sand[800], dot: sand[600] },
  on_hold: { bg: warning[100], fg: warning[800], dot: warning[600] },
} as const;

export const colors = {
  /* Raw scales — kept available for design-system-internal composition.
     Product code should prefer the semantic aliases below. */
  brand: teal,
  sand,
  clay,
  success,
  warning,
  error,
  info,

  /* Semantic surfaces */
  surface: {
    base: sand[50], // page background
    elevated: '#FFFFFF', // cards, modals
    raised: sand[100], // subtle raised panels
    inverted: teal[800], // dark contrast surfaces (footer, hero overlays)
    muted: sand[100], // disabled / secondary panels
    overlay: 'rgba(12, 35, 42, 0.6)', // modal backdrop (teal[900] @ 60%)
  },

  /* Semantic text */
  text: {
    primary: sand[900], // body, headings
    secondary: sand[700], // supporting copy
    muted: sand[500], // hints, captions
    onBrand: '#FFFFFF', // text on brand-500+ surfaces
    onInverted: sand[100], // text on inverted surfaces
    link: teal[600],
    linkHover: teal[700],
    danger: error[600],
    success: success[600],
  },

  /* Semantic borders */
  border: {
    subtle: sand[200],
    default: sand[300],
    strong: sand[400],
    focus: teal[500],
    danger: error[500],
  },

  /* Feedback (alerts, toasts) */
  feedback: {
    success: success[500],
    successBg: success[50],
    warning: warning[500],
    warningBg: warning[50],
    error: error[500],
    errorBg: error[50],
    info: info[500],
    infoBg: info[50],
  },

  /* Status (state-machine) */
  status,

  /* Performance lifestyle accents (marketing only) */
  accent: {
    clay: clay[500],
    clayBg: clay[50],
    teal: teal[500],
    tealDeep: teal[700],
  },
} as const;

export type ColorTokens = typeof colors;
export type StatusKey = keyof typeof status;
